import { MARKETPLACE_AI_COURSES_SEARCH_URL } from '@/config/weblinks'
import { useUserContext } from '@/shared/contexts/userContext'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import BookOutlinedIcon from '@mui/icons-material/BookOutlined'
import HomeOutlined from '@mui/icons-material/HomeOutlined'
import MenuOutlined from '@mui/icons-material/MenuOutlined'
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined'
import ViewSidebarOutlined from '@mui/icons-material/ViewSidebarOutlined'
import { ComponentProps, useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useOnClickOutside } from 'usehooks-ts'
import { useFeatureFlags } from '../hooks/useFeatureFlags'
import { useIsMobile } from '../hooks/useMobile'
import { useSidebar } from '../hooks/useSidebar'
import { cn } from '../lib/utils'
import { MydraDuoLogo } from './MydraDuoLogo'
import { ProfileUpdateReminderTooltip } from './ProfileUpdateReminderTooltip'
import { Typography } from './ui/typography'
import { UserMenu } from './UserMenu'

type AppSidebarProps = ComponentProps<'div'> & {
  showUserMenu?: boolean
  noSidebarToggle?: boolean
}

export function AppSidebar({
  showUserMenu,
  noSidebarToggle,
  ...props
}: AppSidebarProps) {
  const { isLoading, user } = useUserContext()
  const { isCollapsed, isOpen, toggleOpen, toggleCollapsed, setIsOpen } =
    useSidebar()
  const [showText, setShowText] = useState(true)
  const isMobile = useIsMobile()
  const [isRecommendationsPageEnabled] = useFeatureFlags([
    'learningRecommendationsPage',
  ])

  const ref = useRef(null)

  useEffect(() => {
    let timer: NodeJS.Timeout
    if (isCollapsed) {
      setShowText(false)
    } else {
      if (!showText) {
        setShowText(false)
        timer = setTimeout(() => {
          setShowText(true)
        }, 300) // the duration of the transition should match the tailwind transition duration below
      }
    }
    return () => clearTimeout(timer)
  }, [isCollapsed, showText])

  const links = [
    {
      label: !isLoading && user ? 'Dashboard' : 'Home',
      href: !isLoading && user ? '/dashboard' : '/',
      icon: HomeOutlined,
    },
    {
      label: 'Saved Playbooks',
      href: '/playbooks',
      icon: BookOutlinedIcon,
    },
    ...(isRecommendationsPageEnabled
      ? [
          {
            label: 'Learning Recommendations',
            href: '/learning-recommendations',
            icon: SchoolOutlinedIcon,
          },
        ]
      : []),
    {
      label: 'AI Learning',
      href: MARKETPLACE_AI_COURSES_SEARCH_URL,
      icon: AutoAwesomeOutlinedIcon,
      target: '_blank',
    },
  ]

  const handleClickOutside = () => {
    setIsOpen(false)
  }

  useOnClickOutside(ref, handleClickOutside)

  return (
    <div className="AppSidebar" {...props} ref={ref}>
      {!noSidebarToggle && (
        <ProfileUpdateReminderTooltip hidden={!isMobile || isOpen}>
          <div className="absolute left-0 top-0 z-50 px-3 py-4 md:hidden">
            <ToggleAppSidebarButton />
          </div>
        </ProfileUpdateReminderTooltip>
      )}

      <div
        className={cn(
          'flex flex-col px-2 py-4',
          'absolute inset-0 bottom-0 z-40 h-full w-full max-w-60 bg-grey-04 text-white md:relative',
          `transition-[width,transform] duration-300 ease-in-out`,
          isOpen ? 'translate-x-0' : '-translate-x-full',
          isCollapsed ? 'md:w-[56px]' : 'md:w-60',
          'md:translate-x-0'
        )}
      >
        <header className={isCollapsed ? 'p-3 md:p-0' : 'p-3'}>
          <ToggleAppSidebarButton className="text-white md:hidden" />
          <div className="hidden items-center justify-between gap-2 md:flex">
            <MydraDuoLogo className={cn('h-[26px]', isCollapsed && 'hidden')} />
            <button
              onClick={toggleCollapsed}
              className={cn(
                'rounded-sm p-2',
                'flex scale-x-[-1] items-center justify-center bg-primary-light',
                isCollapsed && 'hidden'
              )}
            >
              <ViewSidebarOutlined sx={{ fontSize: 18 }} className="shrink-0" />
            </button>
          </div>
        </header>
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex flex-col gap-2">
            {isCollapsed && (
              <button
                className={cn(
                  'mt-2 hidden items-center justify-center rounded-md px-2 py-3 transition-colors md:flex',
                  'hover:bg-primary-light'
                )}
                onClick={toggleCollapsed}
              >
                <ViewSidebarOutlined
                  sx={{ fontSize: 18 }}
                  className="shrink-0 scale-x-[-1]"
                />
              </button>
            )}
            {links.map((link) => (
              <NavLink
                to={link.href}
                target={link.target}
                key={link.href}
                onClick={toggleOpen}
                className={({ isActive }: { isActive: boolean }) =>
                  cn(
                    'block rounded-md p-3 pr-0 transition-colors',
                    isActive && 'bg-primary-light hover:bg-primary-light'
                  )
                }
              >
                {({ isActive }) => (
                  <div
                    className={cn(
                      'flex items-center gap-2',
                      isCollapsed && 'justify-center'
                    )}
                  >
                    <link.icon
                      className={cn(
                        isActive ? 'text-white' : 'text-mydra-gray'
                      )}
                      sx={{ fontSize: 18 }}
                    />

                    <Typography
                      aria-label={link.label}
                      className="hidden text-sm text-white md:block"
                    >
                      {!isCollapsed && showText ? link.label : <>&nbsp;</>}
                    </Typography>
                    <Typography
                      aria-label={link.label}
                      className="text-sm text-white md:hidden"
                    >
                      {link.label}
                    </Typography>
                  </div>
                )}
              </NavLink>
            ))}
          </div>
        </div>

        <footer className="mt-20 shrink-0 md:mt-auto">
          <ProfileUpdateReminderTooltip hidden={isMobile && !isOpen}>
            {isMobile ? (
              <div>
                <UserMenu user={user} variant="icon-text" />
              </div>
            ) : (
              <div>
                <UserMenu
                  user={user}
                  variant={!isCollapsed && showText ? 'icon-text' : 'icon'}
                />
              </div>
            )}
          </ProfileUpdateReminderTooltip>
        </footer>
      </div>
    </div>
  )
}

type ToggleButtonProps = ComponentProps<'button'>

export function ToggleAppSidebarButton({ ...props }: ToggleButtonProps) {
  const { isOpen, toggleOpen } = useSidebar()

  return (
    <button
      {...props}
      onClick={toggleOpen}
      className={cn(
        'relative z-20 flex items-center justify-center',
        props.className
      )}
      aria-label={isOpen ? 'Close menu' : 'Open menu'}
    >
      <MenuOutlined sx={{ fontSize: 18 }} />
    </button>
  )
}
